import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, MapPin, ArrowRight, CheckCircle2, CreditCard } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getQuote, createBooking, autocomplete, createCheckout, getRates } from "@/lib/api";

const VEHICLES = ["Chevrolet Suburban Z71", "Cadillac Escalade"];
const SERVICES = ["Airport Transfer", "Point to Point", "Hourly / As-Directed", "Hamptons Run"];

const fieldCls =
  "w-full bg-transparent border-b border-white/20 pb-3 pt-1 text-[#F5F5F0] placeholder:text-[#5a5a5a] focus:border-gold focus:outline-none transition-colors duration-300";

const AddressField = ({ label, value, onChange, testid }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const handle = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.length < 3) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await autocomplete(v);
        setSuggestions(res);
        setOpen(true);
      } catch { setSuggestions([]); }
    }, 350);
  };

  return (
    <div className="relative">
      <label className="overline text-[#8A8A8A] block mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <MapPin className="w-4 h-4 text-gold shrink-0" />
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => handle(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="Start typing an address…"
          className={fieldCls}
          autoComplete="off"
        />
      </div>
      <AnimatePresence>
        {open && suggestions.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute z-30 mt-2 w-full bg-[#0d0d0d] border border-white/10 max-h-60 overflow-auto"
          >
            {suggestions.map((s, i) => (
              <li
                key={i}
                data-testid={`${testid}-suggestion-${i}`}
                onMouseDown={() => { onChange(s.label); setOpen(false); }}
                className="px-4 py-3 text-sm text-[#c9c9c4] hover:bg-surface hover:text-gold cursor-pointer border-b border-white/5"
              >
                {s.label}
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const BookingSection = ({ settings }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service_type: "Airport Transfer",
    pickup_address: "", dropoff_address: "", pickup_datetime: "",
    passengers: 2, vehicle: "Chevrolet Suburban Z71", notes: "",
  });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [paying, setPaying] = useState(false);
  const [rates, setRates] = useState([]);
  const [rateId, setRateId] = useState("");

  useEffect(() => { getRates().then(setRates).catch(() => {}); }, []);

  const priceForRate = (rate, vehicle) => {
    if (!rate) return null;
    const p = /escalade/i.test(vehicle) ? rate.escalade_price : rate.suburban_price;
    return p && p > 0 ? p : null;
  };

  const selectRoute = (id) => {
    setRateId(id);
    const rate = rates.find((r) => r.id === id);
    if (!rate) { setQuote(null); return; }
    setForm((f) => ({ ...f, pickup_address: rate.from_label, dropoff_address: rate.to_label, service_type: "Airport Transfer" }));
    const p = priceForRate(rate, form.vehicle);
    setQuote(p ? { estimated_price: p, pricing: "flat", distance_miles: null, duration_min: null } : null);
    if (!p) toast.message("This route has no price yet — we'll confirm your fare.");
  };

  const handlePayNow = async () => {
    if (!done?.id) return;
    setPaying(true);
    try {
      const { checkout_url } = await createCheckout({
        booking_id: done.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "vehicle") {
      if (rateId) {
        const rate = rates.find((r) => r.id === rateId);
        const p = priceForRate(rate, v);
        setQuote(p ? { estimated_price: p, pricing: "flat", distance_miles: null } : null);
      } else {
        setQuote(null);
      }
    } else if (k.includes("address")) {
      setRateId("");
      setQuote(null);
    }
  };

  const handleQuote = async () => {
    if (!form.pickup_address || !form.dropoff_address) {
      toast.error("Enter both pickup and drop-off addresses.");
      return;
    }
    setQuoting(true);
    try {
      const q = await getQuote({
        pickup_address: form.pickup_address,
        dropoff_address: form.dropoff_address,
        vehicle: form.vehicle,
      });
      setQuote(q);
      toast.success(`Estimated ${q.distance_miles} mi · $${q.estimated_price}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not estimate this route.");
    } finally { setQuoting(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup_datetime) {
      toast.error("Name, phone and pickup time are required.");
      return;
    }
    setSubmitting(true);
    try {
      const booking = await createBooking({
        ...form,
        passengers: Number(form.passengers),
      });
      setDone(booking);
      toast.success("Reservation request received.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Call us directly.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <section id="book" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center border border-gold/30 bg-surface p-10 lg:p-16"
          data-testid="booking-confirmation"
        >
          <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" />
          <p className="overline mb-3">Confirmation {done.ref}</p>
          <h2 className="font-serif text-4xl lg:text-5xl mb-5">You&apos;re on the manifest.</h2>
          <p className="text-[#8A8A8A] font-light mb-8">
            We received your request{done.estimated_price ? ` — estimated fare $${done.estimated_price}` : ""}.
            We&apos;ll text you shortly to confirm the details.
          </p>
          {(() => {
            const body = encodeURIComponent(
              `Hi LUXERIDENY, I'd like to pay${done.estimated_price ? ` $${done.estimated_price}` : ""} for booking ${done.ref}.`
            );
            return (
              <a
                href={`sms:+15162622059?&body=${body}`}
                data-testid="pay-now-btn"
                className="group inline-flex items-center gap-3 bg-gold text-black px-8 py-4 hover:tracking-wider transition-[letter-spacing] duration-500"
              >
                <CreditCard className="w-4 h-4" />
                <span className="overline text-black">
                  {done.estimated_price ? `Text to Pay $${done.estimated_price}` : "Text (516) 262-2059 to Pay"}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            );
          })()}
        </motion.div>
      </section>
    );
  }

  return (
    <section id="book" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="overline mb-5">Reserve</p>
          <h2 className="font-serif text-4xl lg:text-6xl tracking-tight leading-tight">
            Request your ride &amp; get an instant estimate.
          </h2>
          <p className="mt-6 text-[#8A8A8A] font-light">
            Enter your route and we&apos;ll auto-calculate the mileage and fare in seconds.
            Prefer to talk? Call{" "}
            <a href={`tel:${settings?.phone}`} className="text-gold">{settings?.phone}</a>.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="lg:col-span-8 bg-surface border border-white/10 p-7 lg:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AddressField label="Pickup Address" value={form.pickup_address}
              onChange={(v) => set("pickup_address", v)} testid="input-pickup" />
            <AddressField label="Drop-off Address" value={form.dropoff_address}
              onChange={(v) => set("dropoff_address", v)} testid="input-dropoff" />

            <div>
              <label className="overline text-[#8A8A8A] block mb-2">Vehicle</label>
              <Select value={form.vehicle} onValueChange={(v) => set("vehicle", v)}>
                <SelectTrigger data-testid="select-vehicle" className="rounded-none bg-transparent border-0 border-b border-white/20 px-0 h-auto pb-3 text-[#F5F5F0] focus:ring-0 focus:border-gold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0d0d0d] border-white/10 text-[#F5F5F0]">
                  {(settings?.vehicles || VEHICLES).map((v) => (
                    <SelectItem key={v} value={v} className="focus:bg-surface focus:text-gold">{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-[#8A8A8A] block mb-2">Service Type</label>
              <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                <SelectTrigger data-testid="select-service" className="rounded-none bg-transparent border-0 border-b border-white/20 px-0 h-auto pb-3 text-[#F5F5F0] focus:ring-0 focus:border-gold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0d0d0d] border-white/10 text-[#F5F5F0]">
                  {SERVICES.map((s) => (
                    <SelectItem key={s} value={s} className="focus:bg-surface focus:text-gold">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Estimate strip */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-black/40 p-5">
            <div>
              {quote ? (
                <div className="flex items-baseline gap-6" data-testid="quote-result">
                  <span className="font-serif text-3xl text-gold">${quote.estimated_price}</span>
                  <span className="text-sm text-[#8A8A8A]">
                    {quote.pricing === "flat" ? "Flat rate · all-in, one way" : `${quote.distance_miles} mi · ~${quote.duration_min} min`}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-[#8A8A8A]">
                  {rateId ? "We'll confirm your fare on request" : "Get your fare estimate before reserving"}
                </span>
              )}
            </div>
            {!rateId && (
              <button
                type="button" onClick={handleQuote} disabled={quoting}
                data-testid="get-estimate-btn"
                className="inline-flex items-center gap-2 border border-gold/50 text-gold px-6 py-3 hover:bg-gold hover:text-black transition-colors duration-400 disabled:opacity-50"
              >
                {quoting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <span className="overline">{quoting ? "Calculating" : "Calculate Fare"}</span>
              </button>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="overline text-[#8A8A8A] block mb-2">Full Name</label>
              <input data-testid="input-name" className={fieldCls} value={form.name}
                onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
            </div>
            <div>
              <label className="overline text-[#8A8A8A] block mb-2">Phone</label>
              <input data-testid="input-phone" className={fieldCls} value={form.phone}
                onChange={(e) => set("phone", e.target.value)} placeholder="(516) 000-0000" />
            </div>
            <div>
              <label className="overline text-[#8A8A8A] block mb-2">Pickup Date &amp; Time</label>
              <input type="datetime-local" data-testid="input-datetime"
                className={`${fieldCls} [color-scheme:dark]`} value={form.pickup_datetime}
                onChange={(e) => set("pickup_datetime", e.target.value)} />
            </div>
            <div>
              <label className="overline text-[#8A8A8A] block mb-2">Passengers</label>
              <input type="number" min="1" max="7" data-testid="input-passengers"
                className={fieldCls} value={form.passengers}
                onChange={(e) => set("passengers", e.target.value)} />
            </div>
          </div>

          <div className="mt-8">
            <label className="overline text-[#8A8A8A] block mb-2">Notes (flight #, luggage, child seat)</label>
            <input data-testid="input-notes" className={fieldCls} value={form.notes}
              onChange={(e) => set("notes", e.target.value)} placeholder="Optional" />
          </div>

          <button
            type="submit" disabled={submitting}
            data-testid="booking-submit-btn"
            className="group mt-10 w-full flex items-center justify-center gap-3 bg-gold text-black py-5 hover:tracking-widest hover:bg-gold-hover transition-[letter-spacing,background-color] duration-500 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            <span className="overline text-black">{submitting ? "Sending" : "Request Reservation"}</span>
            {!submitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>
      </div>
    </section>
  );
};

export default BookingSection;
